import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/store/Reveal";
import { optImg } from "@/lib/img";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Blog de mascotas | The Pet Shopping Co Costa Rica",
    description: "Consejos, guías y cuidados para perros, gatos y más. Aprende a cuidar mejor a tu mascota con los expertos de The Pet Shopping Co.",
    canonicalPath: "/blog",
    jsonLd: { "@context": "https://schema.org", "@type": "Blog", name: "Blog The Pet Shopping Co" },
  });

  useEffect(() => {
    api.get("/blog").then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="max-w-6xl mx-auto px-5 pt-12 sm:pt-16 pb-24">
      <Reveal>
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-coral">Blog</span>
        <h1 className="font-display text-4xl sm:text-6xl font-bold text-charcoal tracking-tight mt-3">Consejos para tu mascota</h1>
        <p className="text-muted-moss mt-3 text-lg max-w-xl">Guías prácticas de alimentación, cuidado y bienestar animal, escritas pensando en ti y tu compañero.</p>
      </Reveal>

      {loading ? (
        <div className="grid place-items-center py-24 text-muted-moss">Cargando artículos...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-24 text-muted-moss">
          <Newspaper className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-semibold">Pronto publicaremos nuevos artículos</p>
        </div>
      ) : (
        <>
          {featured && (
            <Reveal className="mt-10">
              <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-2 rounded-[2rem] overflow-hidden border border-[#E3E7E2] card-shadow bg-white">
                <div className="aspect-[16/10] md:aspect-auto overflow-hidden bg-cream-2">
                  <img src={optImg(featured.cover_image, 800)} alt={featured.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <span className="text-xs font-semibold tracking-wide uppercase text-coral">{featured.category}</span>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-charcoal mt-2 leading-tight">{featured.title}</h2>
                  <p className="text-charcoal/70 mt-3 line-clamp-3">{featured.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-coral group-hover:gap-2.5 transition-all">Leer artículo <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            </Reveal>
          )}

          <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {rest.map((p) => (
              <StaggerItem key={p.id}>
                <Link to={`/blog/${p.slug}`} className="group block h-full rounded-3xl overflow-hidden border border-[#E3E7E2] card-shadow bg-white hover:-translate-y-1 transition-transform">
                  <div className="aspect-[16/10] overflow-hidden bg-cream-2"><img src={optImg(p.cover_image, 500)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5">
                    <span className="text-xs font-semibold tracking-wide uppercase text-coral">{p.category}</span>
                    <h3 className="font-display text-lg font-bold text-charcoal mt-1 line-clamp-2 leading-snug">{p.title}</h3>
                    <p className="text-sm text-muted-moss mt-2 line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </>
      )}
    </div>
  );
}
